import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { sortBy } from 'lodash-es';
import { Dialog } from '../Dialog';
import { interpolate, sanitize } from '../../lib/filename';
import { useSendToDeere } from './send-to-deere-context';
import * as service from '../../services/deere-service';
import { useGrowers } from '../../hooks/use-growers';
import { useAlerts } from '../../hooks/use-alerts';

const MAX_FILENAME_SIZE = 25;
const COMMON_TOKENS = [
  'fieldName',
  'fieldId',
  'name',
  'year',
  'timing',
  'product',
  'instance',
];

function nullMapper(options, rx) {
  return rx;
}

export function SendToDeereDialog() {
  const { showError, showInfo } = useAlerts();
  const { selectedId: growerId, selectedName: growerName } = useGrowers();
  const { dialogProps, close } = useSendToDeere();

  const {
    isOpen,
    Options,
    type,
    pattern,
    prescriptionMapper = nullMapper,
  } = dialogProps;

  const [zipFilename, setZipFilename] = useState('');
  const [filePattern, setFilePattern] = useState('');
  const [transientOptions, setTransientOptions] = useState(null);
  const [options, setOptions] = useState(dialogProps?.options);

  const prescriptions = useMemo(
    () =>
      (dialogProps?.prescriptions || [])
        .flatMap(prescriptionMapper.bind(null, options))
        .filter(Boolean)
        .map((rx) => {
          const {
            fieldName,
            fieldId,
            name,
            timing,
            product,
            nutrient,
            instance,
            rowSpacing: spacing,
          } = rx;
          const aaid = rx.aaManagedId || rx.aaid;
          const id = [rx.id, aaid].filter(Boolean).join('|');
          const year = `${new Date().getFullYear()}`.slice(-2);

          return {
            id,
            aaid,
            fieldName,
            fieldId,
            name,
            year,
            spacing,
            timing,
            product,
            nutrient,
            instance,
          };
        }),
    [dialogProps?.prescriptions, prescriptionMapper, options],
  );

  const availableTokens = useMemo(
    () => COMMON_TOKENS.filter((t) => prescriptions.some((rx) => rx[t])),
    [prescriptions],
  );

  const withFilenames = useMemo(
    () =>
      prescriptions
        .map(buildFilename.bind(null, filePattern))
        .map(validateFilename),
    [prescriptions, filePattern],
  );

  useEffect(() => {
    const year = `${new Date().getFullYear()}`.slice(-2);
    if (type) {
      setZipFilename(`${growerName}_${type}_${year}.zip`);
      setFilePattern(pattern || `{fieldName}_${type}_{year}`);
    }
  }, [pattern, type, growerName]);

  if (!isOpen) return null;

  return (
    <Dialog
      width="sm"
      title="Send to John Deere"
      onCancel={onCancel}
      saveLabel={Options && !options ? 'Next' : undefined}
      canSave={canSave()}
      onSave={onSave}
    >
      {Options && !options ? buildOptionsSelector() : buildFilenamesBuilder()}
    </Dialog>
  );

  function canSave() {
    return Options && !options ? validateOptions() : validateFilenames();
  }

  function validateOptions() {
    return Boolean(transientOptions);
  }

  function validateFilenames() {
    return !withFilenames.some((rx) => rx.validationErrors.length);
  }

  async function onSave() {
    if (Options && !options) return saveTransientOptions();

    try {
      await service.sendPrescriptions(
        growerId,
        type,
        sanitize(zipFilename),
        withFilenames,
      );
      showInfo(`${zipFilename} was sent`);
      onCancel();
    } catch (e) {
      showError(e);
    }
  }

  function resetState() {
    setZipFilename('');
    setFilePattern('');
    setTransientOptions(null);
    setOptions(null);
  }

  function onCancel() {
    resetState();
    close();
  }

  function saveTransientOptions() {
    setTransientOptions(null);
    setOptions(transientOptions);
  }

  function buildOptionsSelector() {
    return (
      <Options
        prescriptions={dialogProps.prescriptions}
        setOptions={setTransientOptions}
      />
    );
  }

  function buildTokens() {
    return (
      <Box sx={{ display: 'flex', gap: 0.5, mt: -1, mb: 3 }}>
        {availableTokens.map(buildToken)}
      </Box>
    );
  }

  function buildToken(name) {
    return (
      <Tooltip key={name} title={`Add {${name}} to the file name pattern`}>
        <Chip
          sx={{ fontFamily: 'monospace' }}
          color="info"
          size="small"
          label={name}
          onClick={insertToken.bind(null, name)}
        />
      </Tooltip>
    );
  }

  function insertToken(token) {
    setFilePattern((current) => `${current}{${token}}`);
  }

  function buildFilenamesBuilder() {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <TextField
          label="Zip File Name"
          variant="filled"
          value={zipFilename}
          sx={{ width: '100%', mt: 0.5, mb: 2 }}
          onChange={(e) => setZipFilename(sanitize(e.target.value))}
          InputProps={{
            endAdornment: (
              <InputHelp>
                The name of the zip file that will be added to Operations Center
                - Files
              </InputHelp>
            ),
          }}
        />
        <TextField
          label="File Name Pattern"
          variant="filled"
          value={filePattern}
          sx={{ width: '100%', mt: 0.5, mb: 2 }}
          onChange={(e) => setFilePattern(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputHelp>
                “File Name Pattern” refers to individual file names within the
                Zip folder. Anything between &#123; and &#125; will act as a
                replacement variable. If the file name is longer then the
                maximum length, characters will be removed from the middle of
                the variables. You may add variables using the buttons below or
                manually enter them.
              </InputHelp>
            ),
          }}
        />
        {buildTokens()}
        <TableContainer sx={{ flex: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Field Name</TableCell>
                <TableCell>File Name</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortBy(withFilenames, 'fieldName').map((rx, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Typography>{rx.fieldName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography>{rx.filename}</Typography>
                      {rx.validationErrors?.length ? (
                        <Typography color="error">
                          {rx.validationErrors.join()}
                        </Typography>
                      ) : null}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }
}

function validateFilename(rx, i, all) {
  const validationErrors = [];

  if (!rx.filename?.length || rx.filename.length > MAX_FILENAME_SIZE) {
    validationErrors.push(`Length must be between 1 and ${MAX_FILENAME_SIZE}`);
  } else {
    const dupes = all.filter(({ filename }) => filename === rx.filename);
    if (dupes.length > 1) validationErrors.push('Cannot be duplicated');
  }

  return { ...rx, validationErrors };
}

function buildFilename(pattern, rx) {
  const filename =
    pattern && interpolate(pattern, rx, MAX_FILENAME_SIZE, preInterpolater);
  return { ...rx, filename };
}

function InputHelp(props) {
  return (
    <InputAdornment position="end">
      <Tooltip
        placement="top-end"
        slotProps={{
          popper: {
            modifiers: [{ name: 'offset', options: { offset: [12, -12] } }],
          },
          tooltip: {
            sx: {
              maxWidth: 500,
              padding: '16px',
            },
          },
        }}
        title={<Typography variant="body2">{props.children}</Typography>}
      >
        <InfoIcon />
      </Tooltip>
    </InputAdornment>
  );
}

function preInterpolater(filenameReplacements) {
  Object.keys(filenameReplacements).forEach((key) => {
    filenameReplacements[key] = sanitize(filenameReplacements[key]);
  });
  return filenameReplacements;
}
